import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  useCollectionShares,
  useKnownUsers,
  useShareCollection,
  useUnshareCollection,
} from '~/api/hooks/useShares'
import type { VCollection } from '~/api/endpoints/collections'
import type { KnownUser } from '~/api/endpoints/share'
import { errorString } from '~/errors/handleApiError'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

interface Props {
  open: boolean
  onClose: () => void
  collection: VCollection
}

export function CollectionShareDialog({ open, onClose, collection }: Props): React.JSX.Element {
  const { user } = useAuth0()
  const myId = user?.sub ?? ''
  const shares = useCollectionShares(open ? collection.id : null)
  const known = useKnownUsers()
  const share = useShareCollection()
  const unshare = useUnshareCollection()

  const sharedUserIds = new Set(shares.data?.map((s) => s.user_id) ?? [])
  const pickerOptions: KnownUser[] = (known.data ?? []).filter(
    (u) => u.user_auth_id !== myId && !sharedUserIds.has(u.user_auth_id),
  )

  const [pickerValue, setPickerValue] = React.useState<KnownUser | null>(null)

  const handlePick = (_e: unknown, picked: KnownUser | null) => {
    if (!picked) return
    share.mutate({
      collectionId: collection.id,
      userAuthId: picked.user_auth_id,
      userEmail: picked.user_email,
    })
    setPickerValue(null)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{DIALOGS.shareCollectionTitle(collection.name)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          {DIALOGS.sharedWith}
        </Typography>
        {shares.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorString(shares.error)}
          </Alert>
        )}
        {known.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorString(known.error)}
          </Alert>
        )}
        {shares.data?.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
            {DIALOGS.notShared}
          </Typography>
        ) : (
          <List dense>
            {shares.data?.map((s) => (
              <ListItem
                key={s.user_id}
                secondaryAction={
                  <Tooltip title={DIALOGS.stopSharing}>
                    <IconButton
                      edge="end"
                      color="error"
                      aria-label={DIALOGS.stopSharing}
                      onClick={() =>
                        unshare.mutate({ collectionId: collection.id, userAuthId: s.user_id })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText primary={s.user_email ?? s.user_id} />
              </ListItem>
            ))}
          </List>
        )}
        <Divider sx={{ my: 2 }} />
        <Autocomplete
          value={pickerValue}
          onChange={handlePick}
          options={pickerOptions}
          getOptionLabel={(u) => u.user_email}
          isOptionEqualToValue={(a, b) => a.user_auth_id === b.user_auth_id}
          renderInput={(params) => <TextField {...params} label={DIALOGS.addPerson} size="small" />}
        />
        {share.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorString(share.error)}
          </Alert>
        )}
        {unshare.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorString(unshare.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{DIALOGS.close}</Button>
      </DialogActions>
    </Dialog>
  )
}
